import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Clock, Zap, TrendingUp, DollarSign, FileText, Shield, AlertTriangle, Mail, Send, Copy, Check } from 'lucide-react';
import * as apiService from '../services/apiService';

// Patient Financials Sub-Component with Email Integration
const PatientFinancialsResults = ({ data }) => {
  const [emailStatus, setEmailStatus] = useState(null); // null, 'sending', 'sent', 'error'
  const [copied, setCopied] = useState(false);

  const handleSendEmail = async () => {
    // For now, show demo functionality
    setEmailStatus('sending');

    try {
      // This would be called with actual patient data in production
      const result = await apiService.sendPatientEmail({
        emailType: 'balance_reminder',
        patientEmail: 'patient@example.com', // Would come from selected patient
        patientName: 'Patient',
        practiceName: 'Your Practice',
        practicePhone: '555-123-4567',
        balance: data.financialProfile?.currentBalance || '$0.00',
        customMessage: data.sampleMessage
      });

      if (result.success) {
        setEmailStatus('sent');
        setTimeout(() => setEmailStatus(null), 3000);
      } else {
        setEmailStatus('error');
      }
    } catch (error) {
      console.error('Email send error:', error);
      setEmailStatus('error');
    }
  };

  const handleCopyMessage = () => {
    if (data.sampleMessage) {
      navigator.clipboard.writeText(data.sampleMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-4">
      {/* Financial Profile Summary */}
      <div className="p-6 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-orange-600" />
          <h3 className="font-bold text-orange-900">Financial Assessment</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-sm text-orange-600 mb-1">Current Balance</div>
            <div className="text-2xl font-bold text-orange-900">{data.financialProfile?.currentBalance || '$0'}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-orange-600 mb-1">Collection Likelihood</div>
            <div className="text-2xl font-bold text-green-600">{data.financialProfile?.collectionLikelihood || 'N/A'}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-orange-600 mb-1">Risk Score</div>
            <div className={`text-2xl font-bold ${
              data.financialProfile?.riskScore === 'Low' ? 'text-green-600' :
              data.financialProfile?.riskScore === 'Medium' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {data.financialProfile?.riskScore || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Strategy */}
      {data.recommendedStrategy && (
        <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-bold text-blue-900 mb-3">Recommended Strategy</h3>
          <div className="flex items-center gap-3 mb-2">
            <span className="font-semibold text-blue-800">{data.recommendedStrategy.approach}</span>
            <span className={`text-xs px-2 py-1 rounded font-semibold ${
              data.recommendedStrategy.priority === 'High' ? 'bg-red-100 text-red-700' :
              data.recommendedStrategy.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-green-100 text-green-700'
            }`}>
              {data.recommendedStrategy.priority} Priority
            </span>
          </div>
          <p className="text-sm text-blue-700">{data.recommendedStrategy.reasoning}</p>
        </div>
      )}

      {/* Payment Options */}
      {data.paymentOptions && data.paymentOptions.length > 0 && (
        <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
          <h3 className="font-bold text-purple-900 mb-4">Payment Options</h3>
          <div className="space-y-3">
            {data.paymentOptions.map((option, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${
                option.recommended ? 'bg-purple-100 border-purple-300' : 'bg-white border-purple-200'
              }`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-semibold text-purple-900 flex items-center gap-2">
                      {option.plan}
                      {option.recommended && (
                        <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded">Recommended</span>
                      )}
                    </div>
                    <div className="text-sm text-purple-700">{option.terms}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-900">{option.amount}</div>
                    {option.discount && (
                      <div className="text-xs text-green-600">{option.discount}</div>
                    )}
                  </div>
                </div>
                <div className="text-xs text-purple-600">{option.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI-Generated Message with Send Button */}
      {data.sampleMessage && (
        <div className="p-6 bg-teal-50 border border-teal-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              <h3 className="font-bold text-teal-900">AI-Generated Collection Message</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyMessage}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-teal-300 rounded-lg hover:bg-teal-100 transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-teal-600" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailStatus === 'sending'}
                className={`flex items-center gap-1 px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors ${
                  emailStatus === 'sent'
                    ? 'bg-green-600 text-white'
                    : emailStatus === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {emailStatus === 'sending' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : emailStatus === 'sent' ? (
                  <>
                    <Check className="w-4 h-4" />
                    Sent!
                  </>
                ) : emailStatus === 'error' ? (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    Failed
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-teal-200">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{data.sampleMessage}</pre>
          </div>
          <p className="text-xs text-teal-600 mt-2">
            * Email will be sent to the patient's email address on file
          </p>
        </div>
      )}

      {/* Communication Plan */}
      {data.communicationPlan && (
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg">
          <h3 className="font-bold text-slate-900 mb-4">Communication Plan</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-slate-600">Preferred Channel</div>
              <div className="font-semibold text-slate-900">{data.communicationPlan.preferredChannel}</div>
            </div>
            <div>
              <div className="text-sm text-slate-600">Best Time</div>
              <div className="font-semibold text-slate-900">{data.communicationPlan.suggestedTiming}</div>
            </div>
            <div>
              <div className="text-sm text-slate-600">Tone</div>
              <div className="font-semibold text-slate-900">{data.communicationPlan.tone}</div>
            </div>
            <div>
              <div className="text-sm text-slate-600">Follow-up Schedule</div>
              <div className="font-semibold text-slate-900">
                {data.communicationPlan.followUpSchedule?.join(', ') || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escalation Plan */}
      {data.escalationPlan && (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="font-bold text-red-900 mb-3">Escalation Plan</h3>
          <div className="mb-2">
            <span className="text-sm text-red-600">Trigger: </span>
            <span className="text-sm font-semibold text-red-800">{data.escalationPlan.trigger}</span>
          </div>
          <div className="text-sm text-red-700">
            <span className="font-medium">Actions: </span>
            {data.escalationPlan.actions?.join(' → ')}
          </div>
        </div>
      )}
    </div>
  );
};

const SafeSection = ({ children }) => {
  try {
    return children;
  } catch {
    return null;
  }
};

const ResultsDisplay = ({ results, processingTime, onSaveToPatient }) => {
  if (!results) return null;

  const { success, data: rawData, mode } = results;
  // Defensive: ensure data is always an object
  const data = rawData && typeof rawData === 'object' ? rawData : {};

  if (!success) {
    return (
      <div className="mt-6 p-6 bg-red-50 border-2 border-red-200 rounded-lg">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="font-bold text-red-900">Workflow Failed</h3>
            <p className="text-sm text-red-700 mt-1">{results.error || 'An error occurred'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {/* Success Header */}
      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="font-bold text-green-900">Workflow Completed Successfully</h3>
              <div className="flex items-center gap-4 mt-1">
                               {processingTime && (
                  <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Processed in {processingTime}s
                  </span>
                )}
                {data.confidence && (
                  <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-1 rounded flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Confidence: {data.confidence}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Verification Results */}
      {data.coverage && (
        <div className="space-y-4">
          <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-blue-900">Coverage Summary</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-blue-600 font-medium">Carrier</div>
                <div className="text-lg font-bold text-blue-900">{data.coverage.primary.carrier}</div>
              </div>
              <div>
                <div className="text-sm text-blue-600 font-medium">Plan</div>
                <div className="text-lg font-bold text-blue-900">{data.coverage.primary.plan}</div>
              </div>
              <div>
                <div className="text-sm text-blue-600 font-medium">Member ID</div>
                <div className="text-lg font-bold text-blue-900">{data.coverage.primary.memberId}</div>
              </div>
              <div>
                <div className="text-sm text-blue-600 font-medium">Status</div>
                <div className="text-lg font-bold text-green-600">{data.coverage.primary.status}</div>
              </div>
            </div>
          </div>

          {data.coverage.annualMaximum && (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-green-900 mb-4">Annual Maximum</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">Total:</span>
                  <span className="font-bold text-green-900">${data.coverage.annualMaximum.total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">Used:</span>
                  <span className="font-bold text-orange-600">${data.coverage.annualMaximum.used.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">Remaining:</span>
                  <span className="font-bold text-green-600">${data.coverage.annualMaximum.remaining.toLocaleString()}</span>
                </div>
                <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-600 rounded-full transition-all" style={{ width: `${100 - data.coverage.annualMaximum.percentageUsed}%` }}></div>
                </div>
                <div className="text-xs text-green-600 text-center">{data.coverage.annualMaximum.percentageUsed}% Used</div>
              </div>
            </div>
          )}

          {data.coverage.coverageLevels && (
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg">
              <h3 className="font-bold text-slate-900 mb-4">Coverage Levels</h3>
              <div className="space-y-3">
                {data.coverage.coverageLevels.map((level, idx) => (
                  <div key={idx} className="p-4 bg-white rounded-lg border border-slate-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-slate-900">{level.category}</div>
                      <div className="text-lg font-bold text-teal-600">{level.coverage}</div>
                    </div>
                    <div className="text-sm text-slate-600">{level.procedures}</div>
                    {level.notes && <div className="text-xs text-slate-500 mt-1">{level.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Denial Analysis Results */}
      {data.denialCode && (
        <div className="space-y-4">
          <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="font-bold text-red-900">Denial Details</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-red-600 font-medium">Claim ID</div>
                <div className="text-lg font-bold text-red-900">{data.claimId}</div>
              </div>
              <div>
                <div className="text-sm text-red-600 font-medium">Denial Code</div>
                <div className="text-lg font-bold text-red-900">{data.denialCode}</div>
              </div>
              <div className="col-span-2">
                <div className="text-sm text-red-600 font-medium mb-1">Reason</div>
                <div className="text-sm text-red-900">{data.denialReason}</div>
              </div>
            </div>
          </div>

          {data.analysis && (
            <div className="p-6 bg-orange-50 border border-orange-200 rounded-lg">
              <h3 className="font-bold text-orange-900 mb-4">Recovery Analysis</h3>
              {typeof data.analysis === 'string' ? (
                <p className="text-sm text-orange-900">{data.analysis}</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-orange-600 mb-1">Recoverability</div>
                      <div className="text-2xl font-bold text-orange-900">
                        {String(data.analysis.recoverability || 'N/A').toString().replace('%', '')}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-orange-600 mb-1">Time to Appeal</div>
                      <div className="text-2xl font-bold text-orange-900">{data.analysis.timeToAppeal || data.analysis.appealDeadline || 'N/A'}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-orange-600 mb-1">Est. Recovery</div>
                      <div className="text-2xl font-bold text-green-600">
                        ${typeof data.estimatedRecovery === 'number' ? data.estimatedRecovery.toLocaleString() : (data.estimatedRecovery || '0')}
                      </div>
                    </div>
                  </div>
                  {(data.analysis.category || data.analysis.severity) && (
                    <div className="mt-4 pt-4 border-t border-orange-200 grid grid-cols-2 gap-4">
                      {data.analysis.category && (
                        <div><span className="text-sm text-orange-600">Category:</span> <span className="text-sm font-semibold text-orange-900">{data.analysis.category}</span></div>
                      )}
                      {data.analysis.severity && (
                        <div><span className="text-sm text-orange-600">Severity:</span> <span className="text-sm font-semibold text-orange-900">{data.analysis.severity}</span></div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {data.aiRecommendations && (
            <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="font-bold text-purple-900 mb-4">AI Recommendations</h3>
              <div className="space-y-3">
                {Array.isArray(data.aiRecommendations) ? data.aiRecommendations.map((rec, idx) => (
                  <div key={idx} className="p-4 bg-white rounded-lg border border-purple-200">
                    {typeof rec === 'string' ? (
                      <div className="text-sm text-slate-700">{rec}</div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-semibold text-purple-900">{rec.action || rec.recommendation || rec.title || 'Recommendation'}</div>
                          {rec.priority && (
                            <span className={`text-xs px-2 py-1 rounded font-semibold ${rec.priority === 'Critical' ? 'bg-red-100 text-red-700' : rec.priority === 'High' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {rec.priority}
                            </span>
                          )}
                        </div>
                        {(rec.rationale || rec.description || rec.details) && (
                          <div className="text-sm text-slate-600 mb-2">{rec.rationale || rec.description || rec.details}</div>
                        )}
                        <div className="flex items-center justify-between text-xs">
                          {rec.successRate && <span className="text-green-600 font-semibold">Success Rate: {String(rec.successRate).replace('%', '')}%</span>}
                          {rec.timeEstimate && <span className="text-slate-500">{rec.timeEstimate}</span>}
                        </div>
                      </>
                    )}
                  </div>
                )) : (
                  <div className="p-4 bg-white rounded-lg border border-purple-200">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{typeof data.aiRecommendations === 'string' ? data.aiRecommendations : JSON.stringify(data.aiRecommendations, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {(data.suggestedAppealLanguage || data.appealLetterDraft) && (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-green-900">AI-Generated Appeal Letter</h3>
              </div>
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{data.suggestedAppealLanguage || data.appealLetterDraft}</pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pre-Auth Results */}
      {data.authNumber && (
        <div className="space-y-4">
          <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
            <h3 className="font-bold text-purple-900 mb-4">Pre-Authorization Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-purple-600 font-medium">Auth Number</div>
                <div className="text-lg font-bold text-purple-900">{data.authNumber}</div>
              </div>
              <div>
                <div className="text-sm text-purple-600 font-medium">Approval Probability</div>
                <div className="text-lg font-bold text-green-600">{data.approvalProbability}%</div>
              </div>
            </div>
          </div>

          {data.procedureRequested && (
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-bold text-blue-900 mb-4">Procedure</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-blue-600">Code:</span>
                  <span className="font-semibold text-blue-900">{data.procedureRequested.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-blue-600">Description:</span>
                  <span className="font-semibold text-blue-900">{data.procedureRequested.description}</span>
                </div>
                {data.procedureRequested.toothNumber && (
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-600">Tooth #:</span>
                    <span className="font-semibold text-blue-900">{data.procedureRequested.toothNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {data.estimatedCosts && (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-green-900 mb-4">Financial Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-green-600">Procedure Fee:</span>
                  <span className="font-bold text-green-900">${typeof data.estimatedCosts.procedureFee === 'number' ? data.estimatedCosts.procedureFee.toLocaleString() : data.estimatedCosts.procedureFee}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-green-600">Insurance Coverage:</span>
                  <span className="font-bold text-green-600">${typeof data.estimatedCosts.insuranceCoverage === 'number' ? data.estimatedCosts.insuranceCoverage.toLocaleString() : data.estimatedCosts.insuranceCoverage}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-green-200">
                  <span className="text-sm text-green-700 font-semibold">Patient Responsibility:</span>
                  <span className="font-bold text-orange-600">${typeof data.estimatedCosts.patientResponsibility === 'number' ? data.estimatedCosts.patientResponsibility.toLocaleString() : data.estimatedCosts.patientResponsibility}</span>
                </div>
              </div>
            </div>
          )}

          {data.generatedLetter && (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-green-900">AI-Generated Pre-Authorization Letter</h3>
              </div>
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{data.generatedLetter}</pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Coding Results */}
      {data.suggestedCodes && (
        <div className="space-y-4">
          <div className="p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
            <h3 className="font-bold text-indigo-900 mb-4">Suggested CDT Codes</h3>
            <div className="space-y-3">
              {data.suggestedCodes.primary && data.suggestedCodes.primary.map((code, idx) => (
                <div key={idx} className="p-4 bg-white rounded-lg border border-indigo-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-indigo-900">{code.code}</div>
                      <div className="text-sm text-slate-600">{code.description}</div>
                    </div>
                    <div className="text-right">
			<div className="text-xs text-slate-600 mb-1">Expected Reimbursement</div>
                      <div className="text-lg font-bold text-green-600">${code.expectedReimbursement}</div>
                      <div className="text-xs text-teal-600 font-semibold">Confidence: {code.confidence}%</div>
                    </div>
                  </div>
                  {code.denialRisk && (
                    <div className="text-xs text-slate-500 mt-2">Denial Risk: {code.denialRisk}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* OCR Results */}
      {data.extractedData && (
        <div className="space-y-4">
          <div className="p-6 bg-teal-50 border border-teal-200 rounded-lg">
            <h3 className="font-bold text-teal-900 mb-4">Extracted Insurance Information</h3>
            {data.extractedData.memberInfo ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-teal-600 font-medium">Member Name</div>
                  <div className="text-lg font-bold text-teal-900">{data.extractedData.memberInfo.name || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-teal-600 font-medium">Member ID</div>
                  <div className="text-lg font-bold text-teal-900">{data.extractedData.memberInfo.memberId || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-teal-600 font-medium">Group Number</div>
                  <div className="text-lg font-bold text-teal-900">{data.extractedData.memberInfo.groupNumber || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-teal-600 font-medium">Carrier</div>
                  <div className="text-lg font-bold text-teal-900">{data.extractedData.carrierInfo?.name || '—'}</div>
                </div>
              </div>
            ) : (
              <pre className="text-xs text-teal-800 bg-teal-100 p-3 rounded overflow-auto">{JSON.stringify(data.extractedData, null, 2)}</pre>
            )}
          </div>

          {data.dataQualityMetrics && (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-green-900 mb-4">OCR Confidence</h3>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600">{data.dataQualityMetrics.overallAccuracy}%</div>
                <div className="text-sm text-green-700 mt-1">Overall Accuracy</div>
              </div>
            </div>
          )}

          {data.extractedData?.memberInfo && onSaveToPatient && (
            <div className="p-4 bg-white border-2 border-teal-500 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">Save to Patient Record</div>
                  <div className="text-sm text-slate-500 mt-1">Create or update a patient record with the extracted insurance information</div>
                </div>
                <button
                  onClick={() => onSaveToPatient(data.extractedData)}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  Save to Patient →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Claim Validation Results */}
      {data.validationResults && (
        <div className="space-y-4">
          <div className={`p-6 border rounded-lg ${data.validationResults.overall === 'PASS' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <h3 className={`font-bold mb-4 ${data.validationResults.overall === 'PASS' ? 'text-green-900' : 'text-yellow-900'}`}>
              Workflow Results
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
  <span className="text-sm font-medium">Validation Status</span>
  <span className={`font-bold px-3 py-1 rounded ${data.validationResults.readyToSubmit ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
    {data.validationResults.readyToSubmit ? 'Ready to Submit' : 'Needs Review'}
  </span>
</div>
              {data.errors && data.errors.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Errors</span>
                  <span className="font-bold text-red-600">{data.errors.length}</span>
                </div>
              )}
              {data.warnings && data.warnings.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1">Warnings</div>
                  {data.warnings.map((warning, idx) => (
                    <div key={idx} className="text-sm text-yellow-700">{warning}</div>
                  ))}
                </div>
              )}
              {data.suggestions && (
                <div>
                  <div className="text-sm font-medium mb-1">Suggestions</div>
                  {data.suggestions.map((suggestion, idx) => (
                    <div key={idx} className="text-sm text-slate-600">{suggestion}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Patient Financials Results - Enhanced with Email */}
      {data.financialProfile && (
        <PatientFinancialsResults data={data} />
      )}

      {/* Fallback: if no specific section rendered, show raw results */}
      {!data.denialCode && !data.validationResults && !data.authNumber && !data.suggestedCodes && !data.financialProfile && !data.extractedData && !data.coverageDetails && !data.coverage && Object.keys(data).length > 0 && (
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg">
          <h3 className="font-bold text-slate-900 mb-3">AI Response</h3>
          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}

    </div>
  );
};

export default ResultsDisplay;
